/*
  BreakingT — Product 2.0
  ------------------------
  Vanilla JS, no dependencies. Works with sections/bt-p2-main.liquid, bt-p2-rail.liquid and the
  on-demand feed sections/bt-p2-feed.liquid.

  - Variant picking (Style → Color → Size), honest "+$1" size deltas and youth age labels
  - Add to cart through /cart/add.js, then opens the theme's own cart drawer (falls back to /cart)
  - Free-shipping meter (theme threshold + messages), arrival estimate, sticky bar, size sheet, lightbox
  - "Shop our mugs": exact product-type match via /collections/types?q=<type>, mugs only (category
    Mugs + vendor Printful, checked in sections/bt-p2-mugs-feed.liquid); newest few first, then best sellers
  - Team rail: same feed sorted best-selling, loaded when the shopper gets near it
  - Late content is revealed without moving what the shopper is looking at
*/
(() => {
  'use strict';

  const $ = (sel, scope = document) => scope.querySelector(sel);
  const $$ = (sel, scope = document) => Array.from(scope.querySelectorAll(sel));
  const reduceMotion = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  const SMOOTH = reduceMotion ? 'auto' : 'smooth';
  const ROOT_URL = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const isPhone = () => !window.matchMedia || window.matchMedia('(max-width: 989px)').matches;
  const BOOTED_AT = Date.now();

  const esc = (value) => String(value == null ? '' : value)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

  /* ------------------------------------------------------------------ money */
  function formatMoney(cents, format) {
    const fmt = String(format || '${{amount}}').replace(/<[^>]*>/g, '');
    const value = Number(cents) || 0;
    const pattern = /\{\{\s*(\w+)\s*\}\}/;
    const delimit = (precision, thousands = ',', decimal = '.') => {
      const fixed = (value / 100).toFixed(precision);
      const parts = fixed.split('.');
      const whole = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1' + thousands);
      return parts[1] ? whole + decimal + parts[1] : whole;
    };
    const match = fmt.match(pattern);
    if (!match) return fmt;
    let out;
    switch (match[1]) {
      case 'amount_no_decimals': out = delimit(0); break;
      case 'amount_with_comma_separator': out = delimit(2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': out = delimit(0, '.', ','); break;
      case 'amount_with_apostrophe_separator': out = delimit(2, "'", '.'); break;
      case 'amount_with_space_separator': out = delimit(2, ' ', ','); break;
      default: out = delimit(2);
    }
    return fmt.replace(pattern, out);
  }
  const trimZeros = (str) => str.replace(/([.,])00(?=\D*$)/, '');

  /* ------------------------------------------------------------------ sizes */
  const SIZE_SHORT = {
    SMALL: 'S', MEDIUM: 'M', LARGE: 'L', 'X-LARGE': 'XL', XLARGE: 'XL', 'EXTRA LARGE': 'XL',
    'XX-LARGE': '2XL', XXL: '2XL', '2X-LARGE': '2XL', 'XXX-LARGE': '3XL', XXXL: '3XL', '3X-LARGE': '3XL',
    'X-SMALL': 'XS', XSMALL: 'XS'
  };
  const shortSize = (value) => { const up = String(value || '').trim().toUpperCase(); return SIZE_SHORT[up] || up; };
  /* chart labels look like "S", "2XL" or "S (6–7)" — match on the leading size code */
  const chartRow = (chart, value) => (chart && chart.sizes ? chart.sizes.findIndex((label) => String(label).split(/[\s(]/)[0].toUpperCase() === shortSize(value)) : -1);

  /* ----------------------------------------------------------------- header */
  function headerEl() {
    const wrap = document.querySelector('.shopify-section-main-header');
    if (wrap) return wrap;
    const header = document.querySelector('header');
    return header ? (header.closest('.shopify-section') || header) : null;
  }
  function headerOffset() {
    const wrap = headerEl();
    if (!wrap) return 0;
    const candidates = [wrap, $('header', wrap)].filter(Boolean);
    const sticky = candidates.find((el) => { const pos = window.getComputedStyle(el).position; return pos === 'sticky' || pos === 'fixed'; });
    return sticky ? Math.max(0, sticky.getBoundingClientRect().bottom) : 0;
  }

  /* ------------------------------------------------------------------- cart */
  const Cart = {
    async add(items) {
      const res = await fetch(ROOT_URL + 'cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ items })
      });
      let json = {};
      try { json = await res.json(); } catch (e) { /* empty body */ }
      if (!res.ok) throw new Error(json.description || json.message || 'That didn’t go through. Please try again.');
      return json;
    },
    async get() {
      const res = await fetch(ROOT_URL + 'cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } });
      return res.json();
    },
    /* Opens the theme's drawer: assets/cart-ajax.js binds `.openCartDrawer` on body. The trigger is a
       real link to /cart, so if the drawer is off or the theme script is missing it just navigates. */
    openDrawer() {
      let drawerOn = true;
      try { drawerOn = (typeof cartDrawerEnable === 'undefined') ? true : !!cartDrawerEnable; } catch (e) { drawerOn = true; }
      const hasDrawer = !!(window.jQuery && typeof window.buildCart === 'function' && document.querySelector('[data-side-drawer]'));
      if (!hasDrawer || !drawerOn) { window.location.href = ROOT_URL + 'cart'; return; }
      const persistent = document.querySelector('[data-p2-cart-trigger]');
      if (persistent) { persistent.click(); return; }
      const trigger = document.createElement('a');
      trigger.href = ROOT_URL + 'cart';
      trigger.className = 'openCartDrawer';
      trigger.hidden = true;
      document.body.appendChild(trigger);
      trigger.click();
      setTimeout(() => trigger.remove(), 0);
    }
  };

  /* Every time the theme rebuilds its drawer we get the fresh cart (keeps the meter honest when
     shoppers change quantities inside the drawer). */
  function hookThemeCart() {
    const original = window.buildCart;
    if (typeof original !== 'function' || original.__p2) return;
    const wrapped = function (cart) {
      try { document.dispatchEvent(new CustomEvent('bt:p2:cart', { detail: cart })); } catch (e) { /* noop */ }
      return original.apply(this, arguments);
    };
    wrapped.__p2 = true;
    window.buildCart = wrapped;
  }

  /* ------------------------------------------------------------------- feed */
  const feedCache = new Map();
  function loadFeed(type, sort, sectionId = 'bt-p2-feed') {
    const url = ROOT_URL + 'collections/types?q=' + encodeURIComponent(type) +
      '&sort_by=' + encodeURIComponent(sort) + '&section_id=' + encodeURIComponent(sectionId);
    if (!feedCache.has(url)) {
      feedCache.set(url, fetch(url, { credentials: 'same-origin', priority: 'low' })
        .then((res) => { if (!res.ok) throw new Error('feed ' + res.status); return res.text(); })
        .then((html) => $$('[data-p2-card]', new DOMParser().parseFromString(html, 'text/html')))
        .catch((err) => { feedCache.delete(url); throw err; }));
    }
    return feedCache.get(url);
  }
  const sameType = (a, b) => String(a || '').trim().toLowerCase() === String(b || '').trim().toLowerCase();

  /* Reveal late content without pushing around what the shopper is reading.
     `marker` is the element directly after the insertion point.
     - insertion point below the screen → show now (nothing visible moves)
     - insertion point above the screen → show, then scroll by the height it added
     - insertion point on screen → show now only if the page just loaded and nobody has scrolled;
       otherwise wait until it scrolls off screen. */
  function revealWithoutJump(marker, show) {
    const attempt = () => {
      const vh = window.innerHeight || document.documentElement.clientHeight;
      const top = marker ? marker.getBoundingClientRect().top : vh + 1;
      if (top >= vh) { show(); return true; }
      if (top <= 0) {
        show();
        const added = marker.getBoundingClientRect().top - top;
        if (added > 1) window.scrollBy(0, added);
        return true;
      }
      if (window.pageYOffset < 8 && Date.now() - BOOTED_AT < 2500) { show(); return true; }
      return false;
    };
    if (attempt()) return;
    const onScroll = () => { if (attempt()) window.removeEventListener('scroll', onScroll); };
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  function railNav(track, prev, next) {
    if (!track) return;
    const step = () => Math.max(200, track.clientWidth * 0.85);
    const sync = () => {
      const max = track.scrollWidth - track.clientWidth - 2;
      if (prev) prev.disabled = track.scrollLeft <= 2;
      if (next) next.disabled = track.scrollLeft >= max;
    };
    if (prev) prev.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: SMOOTH }));
    if (next) next.addEventListener('click', () => track.scrollBy({ left: step(), behavior: SMOOTH }));
    track.addEventListener('scroll', () => window.requestAnimationFrame(sync), { passive: true });
    sync();
  }

  /* ======================================================================
     Product
     ====================================================================== */
  function initProduct(root) {
    const data = JSON.parse($('[data-p2-json]', root).textContent);

    const money = (c) => formatMoney(c, data.moneyFormat);
    const moneyShort = (c) => trimZeros(money(c));
    const variants = data.variants || [];
    const nOpts = Math.max(1, (data.options || []).length);
    const optionEls = $$('[data-p2-option]', root);
    const fsByIndex = (i) => optionEls.find((fs) => Number(fs.dataset.p2Option) === i) || null;
    const sizeIndex = Number(data.sizeIndex);
    const styleFs = optionEls.find((fs) => fs.dataset.kind === 'style');
    const styleIndex = styleFs ? Number(styleFs.dataset.p2Option) : -1;

    const form = $('form[id^="p2-form-"]', root);
    const select = $('[data-p2-select]', root);
    const atc = $('[data-p2-atc]', root);
    const atcLabel = $('[data-p2-atc-label]', root);
    const atcPrice = $('[data-p2-atc-price]', root);
    const qtyInput = $('[data-p2-qty-input]', root);
    const errorEl = $('[data-p2-error]', root);
    const statusEl = $('[data-p2-status]', root);
    const express = $('[data-p2-express]', root);
    const priceEl = $('[data-p2-price]', root);
    const compareEl = $('[data-p2-compare]', root);
    const saveEl = $('[data-p2-save]', root);
    const noteEl = $('[data-p2-price-note]', root);
    const lowEl = $('[data-p2-low]', root);
    const bar = $('[data-p2-bar]', root);
    const barSel = $('[data-p2-bar-sel]', root);
    const barBtn = $('[data-p2-bar-btn]', root);
    if (!atc || !atcLabel) return;

    const state = { sel: new Array(nOpts).fill(null), qty: 1, variant: null, slide: 0 };
    optionEls.forEach((fs) => {
      const i = Number(fs.dataset.p2Option);
      const checked = $('.p2-chip__input:checked', fs);
      state.sel[i] = checked ? checked.value : null;
    });
    if (!optionEls.length && variants[0]) state.sel = variants[0].o.slice();

    const matches = (v, sel) => sel.every((val, i) => val === null || val === undefined || v.o[i] === val);
    const poolFor = (sel) => variants.filter((v) => matches(v, sel));
    const currentVariant = () => (state.sel.some((v) => v === null) ? null : (variants.find((v) => matches(v, state.sel)) || null));
    const missingIndex = () => state.sel.findIndex((x) => x === null);
    const isRealValue = (x) => x !== null && x !== undefined && x !== '' && x !== 'Default Title';
    const singleIdx = optionEls.filter((fs) => fs.classList.contains('p2-opt--single')).map((fs) => Number(fs.dataset.p2Option));

    const announce = (msg) => { if (statusEl) { statusEl.textContent = ''; window.setTimeout(() => { statusEl.textContent = msg; }, 30); } };
    const showError = (msg) => { if (errorEl) { errorEl.textContent = msg; errorEl.hidden = false; } };
    const hideError = () => { if (errorEl) { errorEl.hidden = true; errorEl.textContent = ''; } };

    /* ---------------- gallery ---------------- */
    const gallery = $('[data-p2-gallery]', root);
    const track = $('[data-p2-track]', root);
    const slides = $$('[data-p2-slide]', root);
    const countEl = $('[data-p2-count]', root);
    const thumbs = $$('[data-p2-thumb]', root);
    const prevBtn = $('[data-p2-prev]', root);
    const nextBtn = $('[data-p2-next]', root);
    const barImg = $('.p2-bar__img', root);

    const slideStep = () => (slides.length > 1 ? slides[1].offsetLeft - slides[0].offsetLeft : (track ? track.clientWidth : 1)) || 1;
    function goToSlide(index, behavior = SMOOTH) {
      if (!track || !slides[index]) return;
      track.scrollTo({ left: slides[index].offsetLeft - slides[0].offsetLeft, behavior });
    }
    function syncSlide() {
      if (!track || !slides.length) return;
      const index = Math.max(0, Math.min(slides.length - 1, Math.round(track.scrollLeft / slideStep())));
      state.slide = index;
      if (countEl) countEl.textContent = String(index + 1);
      thumbs.forEach((t, i) => t.setAttribute('aria-current', i === index ? 'true' : 'false'));
      if (prevBtn) prevBtn.disabled = index === 0;
      if (nextBtn) nextBtn.disabled = index === slides.length - 1;
    }
    if (track) {
      let raf = 0;
      track.addEventListener('scroll', () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(syncSlide); }, { passive: true });
      syncSlide();
    }
    if (prevBtn) prevBtn.addEventListener('click', () => goToSlide(Math.max(0, state.slide - 1)));
    if (nextBtn) nextBtn.addEventListener('click', () => goToSlide(Math.min(slides.length - 1, state.slide + 1)));
    thumbs.forEach((t) => t.addEventListener('click', () => goToSlide(Number(t.dataset.p2Thumb))));
    function showMedia(mediaId) {
      if (!mediaId) return;
      const index = slides.findIndex((s) => Number(s.dataset.mediaId) === Number(mediaId));
      if (index < 0) return;
      if (index !== state.slide) goToSlide(index);
      const img = $('img', slides[index]);
      if (barImg && img) {
        const src = img.currentSrc || img.src;
        if (src) { barImg.removeAttribute('srcset'); barImg.src = src; }
      }
    }

    /* ---------------- size charts ---------------- */
    const chartFor = (style) => (style && data.charts && data.charts[style]) || null;
    const productStyles = styleFs ? $$('.p2-chip', styleFs).map((c) => c.dataset.value) : [];
    const chartKeys = Object.keys(data.charts || {});
    const tabStyles = productStyles.length ? productStyles.filter((s) => chartKeys.indexOf(s) >= 0) : chartKeys;

    /* Which chart applies right now. With a Style option it's the selected style (may have no chart).
       Without one, guess from the title. */
    function currentChartStyle() {
      if (styleIndex >= 0) return state.sel[styleIndex];
      const t = String(data.title || '').toLowerCase();
      if (t.indexOf('youth') >= 0 && t.indexOf('hood') >= 0 && chartFor('Youth Hoodie')) return 'Youth Hoodie';
      if (t.indexOf('youth') >= 0 && chartFor('Youth T-Shirt')) return 'Youth T-Shirt';
      if (t.indexOf('hood') >= 0 && chartFor('Hoodie')) return 'Hoodie';
      if ((t.indexOf('v-neck') >= 0 || t.indexOf('women') >= 0) && chartFor("Women's V-Neck")) return "Women's V-Neck";
      return chartFor('Adult T-Shirt') ? 'Adult T-Shirt' : (tabStyles[0] || null);
    }

    function tableHTML(style, selRow) {
      const chart = chartFor(style);
      if (!chart) return '';
      const cols = Object.keys(chart.rows || {});
      let html = '<table class="p2-table"><thead><tr><th scope="col">Size</th>';
      cols.forEach((c) => { html += '<th scope="col">' + esc(c) + ' (in)</th>'; });
      html += '</tr></thead><tbody>';
      (chart.sizes || []).forEach((size, i) => {
        html += '<tr' + (i === selRow ? ' class="is-sel"' : '') + '><th scope="row">' + esc(size) + '</th>';
        cols.forEach((c) => { html += '<td>' + esc((chart.rows[c] || [])[i] || '–') + '</td>'; });
        html += '</tr>';
      });
      return html + '</tbody></table>';
    }
    const selectedRowFor = (style) => (sizeIndex >= 0 && state.sel[sizeIndex] !== null ? chartRow(chartFor(style), state.sel[sizeIndex]) : -1);

    const fitTable = $('[data-p2-fit-table]', root);
    function renderFitTable() {
      if (!fitTable) return;
      const style = currentChartStyle();
      const html = tableHTML(style, selectedRowFor(style));
      fitTable.innerHTML = html ? '<p class="p2-muted" style="margin-bottom:6px"><strong>' + esc(style) + '</strong></p>' + html : '';
    }

    /* ---------------- options ---------------- */
    function renderSizeSubs() {
      const fs = fsByIndex(sizeIndex);
      if (!fs) return;
      const chart = chartFor(styleIndex >= 0 ? state.sel[styleIndex] : null);
      const base = state.sel.map((v, k) => (k === sizeIndex ? null : v));
      const pool = poolFor(base);
      const minPrice = pool.length ? Math.min.apply(null, pool.map((v) => v.p)) : 0;
      $$('.p2-chip', fs).filter((chip) => !chip.hidden).forEach((chip) => {
        const sub = $('[data-p2-sizesub]', chip);
        if (!sub) return;
        const row = chartRow(chart, chip.dataset.value);
        const label = row >= 0 ? String(chart.sizes[row]) : '';
        const age = (label.match(/\(([^)]+)\)/) || [])[1];
        let text = '';
        if (age) text = age;
        else {
          const v = pool.find((x) => x.o[sizeIndex] === chip.dataset.value);
          if (v && v.p > minPrice) text = '+' + moneyShort(v.p - minPrice);
        }
        sub.textContent = text;
      });
    }

    function renderOptions() {
      optionEls.forEach((fs) => {
        const i = Number(fs.dataset.p2Option);
        let visible = 0;
        $$('.p2-chip', fs).forEach((chip) => {
          const val = chip.dataset.value;
          const prefix = state.sel.slice(0, i).concat([val]);
          const pool = variants.filter((v) => prefix.every((pv, k) => pv === null || v.o[k] === pv));
          const exists = pool.length > 0;
          chip.hidden = !exists;
          if (exists) visible += 1;
          chip.classList.toggle('is-out', exists && !pool.some((v) => v.a));
          const on = state.sel[i] === val;
          const input = $('.p2-chip__input', chip);
          if (input) input.checked = on;
          chip.classList.toggle('is-on', on);
        });
        const grid = $('.p2-opt__grid', fs);
        if (grid && fs.dataset.kind === 'size') grid.style.setProperty('--p2-cols', String(Math.max(1, Math.min(6, visible))));
        const valueEl = $('[data-p2-optval]', fs);
        if (valueEl) {
          valueEl.innerHTML = state.sel[i] === null
            ? '<em>' + esc(i === sizeIndex ? data.sizePrompt : 'Choose ' + String(data.options[i] || '').toLowerCase()) + '</em>'
            : esc(state.sel[i]);
        }
      });
      renderSizeSubs();
    }

    /* ---------------- price / button / bar ---------------- */
    function renderBuy() {
      const v = currentVariant();
      state.variant = v;
      const pool = poolFor(state.sel);
      const shown = v || pool.slice().sort((a, b) => a.p - b.p)[0] || variants[0];
      const missing = missingIndex();
      const sizeMissing = missing >= 0 && missing === sizeIndex;

      if (priceEl && shown) priceEl.textContent = money(shown.p);
      const onSale = !!(shown && shown.c > shown.p);
      if (compareEl) { compareEl.hidden = !onSale; if (onSale) compareEl.textContent = money(shown.c); }
      if (saveEl) { saveEl.hidden = !onSale; if (onSale) saveEl.textContent = 'Save ' + moneyShort(shown.c - shown.p); }
      if (noteEl) noteEl.textContent = state.sel.filter((x, i) => isRealValue(x) && i !== sizeIndex).join(' · ');

      let label;
      let btnLabel;
      if (!v) {
        label = sizeMissing ? data.sizePrompt : 'Choose ' + String(data.options[missing] || 'an option').toLowerCase();
        btnLabel = sizeMissing ? 'Select size' : 'Choose';
        atc.removeAttribute('aria-disabled');
        if (atcPrice) atcPrice.hidden = true;
      } else if (!v.a) {
        label = 'Sold out';
        btnLabel = 'Sold out';
        atc.setAttribute('aria-disabled', 'true');
        if (atcPrice) atcPrice.hidden = true;
      } else {
        label = 'Add to cart';
        btnLabel = 'Add to cart';
        atc.removeAttribute('aria-disabled');
        if (atcPrice) { atcPrice.hidden = false; atcPrice.textContent = money(v.p * state.qty); }
      }
      if (!atc.classList.contains('is-added')) atcLabel.textContent = label;

      if (express) {
        const locked = !v || !v.a;
        express.classList.toggle('is-locked', locked);
        Array.from(express.children).forEach((child) => { child.inert = locked; });
      }
      if (select && v && select.value !== String(v.id)) {
        select.value = String(v.id);
        /* lets Shopify's installment banner / express buttons pick up the new variant */
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }

      if (lowEl) {
        if (v && v.a && v.q) { lowEl.textContent = String(data.lowStockText || '').replace('[qty]', v.q); lowEl.hidden = false; }
        else lowEl.hidden = true;
      }

      if (bar) {
        const parts = state.sel.filter((x, i) => isRealValue(x) && singleIdx.indexOf(i) === -1);
        if (v) parts.unshift(money(v.p));
        if (sizeMissing) parts.push(data.sizePrompt);
        if (barSel) barSel.textContent = parts.join(' · ');
        if (barBtn && !barBtn.classList.contains('is-added')) barBtn.textContent = btnLabel;
      }
    }

    function syncUrl() {
      if (!window.history || !window.history.replaceState) return;
      try {
        const url = new URL(window.location.href);
        if (state.variant) url.searchParams.set('variant', state.variant.id);
        else url.searchParams.delete('variant');
        window.history.replaceState(window.history.state, '', url.toString());
      } catch (e) { /* noop */ }
    }

    function render(changedIndex) {
      renderOptions();
      renderBuy();
      renderFitTable();
      if (changedIndex !== undefined && changedIndex !== sizeIndex) {
        const pool = poolFor(state.sel);
        const withMedia = (state.variant && state.variant.m) ? state.variant : pool.find((v) => v.m);
        if (withMedia) showMedia(withMedia.m);
      }
    }

    root.addEventListener('change', (event) => {
      const input = event.target;
      if (!input.matches || !input.matches('.p2-chip__input')) return;
      const fs = input.closest('[data-p2-option]');
      const i = Number(fs.dataset.p2Option);
      state.sel[i] = input.value;
      for (let j = i + 1; j < nOpts; j += 1) {
        if (state.sel[j] === null) continue;
        const prefix = state.sel.slice(0, j + 1);
        if (variants.some((v) => v.a && matches(v, prefix))) continue;
        if (j === sizeIndex) {
          /* keep a sold-out size visible as sold out; clear it only if it doesn't exist in this style */
          if (!variants.some((v) => matches(v, prefix))) state.sel[j] = null;
        } else {
          const upTo = state.sel.slice(0, j);
          const pick = variants.find((v) => v.a && matches(v, upTo)) || variants.find((v) => matches(v, upTo));
          if (pick) state.sel[j] = pick.o[j];
        }
      }
      hideError();
      render(i);
      syncUrl();
    });

    function promptMissing() {
      const missing = missingIndex();
      const fs = fsByIndex(missing);
      if (!fs) return;
      const top = fs.getBoundingClientRect().top + window.pageYOffset - headerOffset() - 14;
      window.scrollTo({ top: Math.max(0, top), behavior: SMOOTH });
      fs.classList.remove('is-nudge');
      void fs.offsetWidth;
      fs.classList.add('is-nudge');
      window.setTimeout(() => fs.classList.remove('is-nudge'), 950);
      announce(missing === sizeIndex ? 'Pick a size first.' : 'Pick an option first.');
    }

    /* ---------------- quantity ---------------- */
    function setQty(n) {
      const q = Math.max(1, Math.min(99, Math.round(Number(n) || 1)));
      state.qty = q;
      if (qtyInput) qtyInput.value = String(q);
      renderBuy();
    }
    root.addEventListener('click', (event) => {
      if (event.target.closest('[data-p2-qty-minus]')) setQty(state.qty - 1);
      else if (event.target.closest('[data-p2-qty-plus]')) setQty(state.qty + 1);
    });
    if (qtyInput) qtyInput.addEventListener('change', () => setQty(qtyInput.value));

    /* ---------------- add to cart ---------------- */
    let busy = false;
    async function addToCart() {
      const v = currentVariant();
      if (!v) { promptMissing(); return; }
      if (!v.a || busy) return;
      busy = true;
      hideError();
      atc.classList.add('is-busy');
      atc.setAttribute('aria-busy', 'true');
      try {
        await Cart.add([{ id: v.id, quantity: state.qty }]);
        atc.classList.add('is-added');
        atcLabel.textContent = 'Added';
        if (barBtn) { barBtn.classList.add('is-added'); barBtn.textContent = 'Added'; }
        announce('Added to your cart.');
        Cart.openDrawer();
        window.setTimeout(() => {
          atc.classList.remove('is-added');
          if (barBtn) barBtn.classList.remove('is-added');
          renderBuy();
        }, 1800);
      } catch (err) {
        showError(err.message);
      } finally {
        busy = false;
        atc.classList.remove('is-busy');
        atc.removeAttribute('aria-busy');
      }
    }
    if (form) form.addEventListener('submit', (event) => { event.preventDefault(); addToCart(); });
    if (barBtn) barBtn.addEventListener('click', () => {
      const v = currentVariant();
      if (!v) promptMissing();
      else if (v.a) addToCart();
    });
    if (express) {
      express.addEventListener('click', (event) => {
        if (!express.classList.contains('is-locked')) return;
        event.preventDefault();
        event.stopPropagation();
        if (!currentVariant()) promptMissing();
        else announce('That size is sold out.');
      }, true);
    }

    /* ---------------- sticky bar (phones) ---------------- */
    const buyrow = $('[data-p2-buyrow]', root);
    if (bar && buyrow) {
      /* Shown once the main button has scrolled above the screen. A rAF-throttled scroll check (not
         IntersectionObserver) so fast flings and anchor jumps can't skip the state change. */
      let barOn = false;
      let ticking = false;
      const setBar = (on) => {
        if (on === barOn) return;
        barOn = on;
        bar.classList.toggle('is-on', on);
        bar.inert = !on;
        bar.setAttribute('aria-hidden', on ? 'false' : 'true');
        document.documentElement.classList.toggle('p2-bar-on', on);
      };
      const check = () => { ticking = false; setBar(isPhone() && buyrow.getBoundingClientRect().bottom < 0); };
      const onScroll = () => { if (!ticking) { ticking = true; window.requestAnimationFrame(check); } };
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll, { passive: true });
      check();
    }

    /* ---------------- free shipping meter + arrival ---------------- */
    const ship = $('[data-p2-ship]', root);
    function renderShip(totalCents) {
      const fs = data.freeShip || {};
      if (!ship || !fs.enabled || !fs.threshold) return;
      let threshold = Number(fs.threshold);
      const cur = window.Shopify && window.Shopify.currency;
      if (cur && cur.active && data.shopCurrency && cur.active !== data.shopCurrency && cur.rate) {
        threshold = Math.round(threshold * parseFloat(cur.rate));
      }
      const total = Math.max(0, Number(totalCents) || 0);
      const remaining = Math.max(0, threshold - total);
      const msg = $('[data-p2-ship-msg]', ship);
      const fill = $('[data-p2-ship-bar]', ship);
      let html;
      if (total <= 0) html = esc(fs.emptyText || '').replace('[amount]', '<strong>' + esc(moneyShort(threshold)) + '</strong>');
      else if (remaining > 0) html = esc(fs.text || '').replace('||amount||', '<strong>' + esc(money(remaining)) + '</strong>');
      else html = '<strong>' + esc(fs.successText || '') + '</strong>';
      if (msg) msg.innerHTML = html;
      ship.classList.toggle('is-done', total > 0 && remaining === 0);
      if (fill) fill.style.width = Math.min(100, Math.floor((total / threshold) * 100)) + '%';
      ship.hidden = false;
    }
    renderShip(data.freeShip ? data.freeShip.cartTotal : 0);
    document.addEventListener('bt:p2:cart', (event) => { if (event.detail) renderShip(event.detail.total_price); });
    window.addEventListener('pageshow', (event) => {
      if (event.persisted && ship) Cart.get().then((cart) => renderShip(cart.total_price)).catch(() => {});
    });

    const eta = $('[data-p2-eta]', root);
    if (eta && data.eta && data.eta.show) {
      // Calendar days from today, weekends included (USPS delivers Saturdays).
      const addDays = (from, days) => { const d = new Date(from.getTime()); d.setDate(d.getDate() + days); return d; };
      const now = new Date();
      const minDays = Number(data.eta.min) || 0;
      const a = addDays(now, minDays);
      const b = addDays(now, Math.max(minDays, Number(data.eta.max) || 0));
      const md = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' });
      const dOnly = new Intl.DateTimeFormat('en-US', { day: 'numeric' });
      const range = a.getMonth() === b.getMonth() ? md.format(a) + '–' + dOnly.format(b) : md.format(a) + ' – ' + md.format(b);
      eta.textContent = (data.eta.prefix ? data.eta.prefix + ' ' : '') + range;
    }

    /* arrival info tooltip: CSS handles hover/keyboard; tap toggles it on touch screens */
    const tip = $('[data-p2-tip]', root);
    const tipBtn = tip && $('[data-p2-tip-btn]', tip);
    if (tip && tipBtn) {
      const row = tip.parentElement;
      const place = () => { row.style.setProperty('--p2-tip-x', Math.round(tipBtn.offsetLeft + tipBtn.offsetWidth / 2) + 'px'); };
      let tipY = 0;
      const setTip = (open) => {
        if (open) { place(); tipY = window.scrollY; }
        tip.classList.toggle('is-open', open);
        tipBtn.setAttribute('aria-expanded', String(open));
      };
      place();
      tip.addEventListener('pointerenter', place);
      tipBtn.addEventListener('focus', place);
      tipBtn.addEventListener('click', (event) => { event.preventDefault(); setTip(!tip.classList.contains('is-open')); });
      document.addEventListener('click', (event) => { if (!tip.contains(event.target)) setTip(false); });
      document.addEventListener('keydown', (event) => { if (event.key === 'Escape' && tip.classList.contains('is-open')) { setTip(false); tipBtn.focus(); } });
      window.addEventListener('scroll', () => { if (tip.classList.contains('is-open') && Math.abs(window.scrollY - tipY) > 60) setTip(false); }, { passive: true });
    }

    /* ---------------- size guide sheet ---------------- */
    const sheet = $('[data-p2-size-sheet]', root);
    const sizeTabs = $('[data-p2-size-tabs]', root);
    const sizeTable = $('[data-p2-size-table]', root);
    const sizePage = $('[data-p2-size-page]', root);

    function renderSheet(style) {
      const html = tableHTML(style, selectedRowFor(style));
      const showTabs = !!html && tabStyles.length > 1;
      if (sizeTabs) {
        sizeTabs.innerHTML = showTabs ? tabStyles.map((s) =>
          '<button type="button" class="p2-tab" role="tab" aria-selected="' + (s === style) + '" data-p2-tab="' + esc(s) + '">' + esc(s) + '</button>').join('') : '';
        sizeTabs.hidden = !showTabs;
      }
      if (sizeTable) {
        sizeTable.innerHTML = html || (sizePage ? '' : '<p class="p2-muted">Exact measurements for this style aren’t listed yet. It fits true to size, and exchanges are free for 60 days.</p>');
      }
      if (sizePage) sizePage.hidden = !!html;
    }
    if (sizeTabs) sizeTabs.addEventListener('click', (event) => {
      const tab = event.target.closest('[data-p2-tab]');
      if (tab) renderSheet(tab.dataset.p2Tab);
    });

    /* ---------------- dialogs ---------------- */
    function openDialog(dialog) {
      if (!dialog) return;
      if (typeof dialog.showModal === 'function') { if (!dialog.open) dialog.showModal(); }
      else dialog.setAttribute('open', '');
      document.documentElement.style.overflow = 'hidden';
    }
    function closeDialog(dialog) {
      if (!dialog) return;
      if (typeof dialog.close === 'function' && dialog.open) dialog.close();
      else dialog.removeAttribute('open');
      document.documentElement.style.overflow = '';
    }
    $$('dialog', root).forEach((dialog) => {
      dialog.addEventListener('click', (event) => {
        if (event.target === dialog || event.target.closest('[data-p2-close]')) closeDialog(dialog);
      });
      dialog.addEventListener('close', () => { document.documentElement.style.overflow = ''; });
    });

    root.addEventListener('click', (event) => {
      if (event.target.closest('[data-p2-open-size]')) {
        event.preventDefault();
        renderSheet(currentChartStyle());
        openDialog(sheet);
      }
    });

    const lightbox = $('[data-p2-lightbox]', root);
    const lightTrack = $('[data-p2-lightbox-track]', root);
    if (lightbox && lightTrack) {
      root.addEventListener('click', (event) => {
        const zoom = event.target.closest('[data-p2-zoom]');
        if (!zoom) return;
        const index = Number(zoom.dataset.p2Zoom) || 0;
        $$('img[data-src]', lightTrack).forEach((img, i) => {
          if (Math.abs(i - index) > 1) img.loading = 'lazy';
          img.src = img.dataset.src;
          img.removeAttribute('data-src');
        });
        openDialog(lightbox);
        const slide = lightTrack.children[index];
        if (slide) lightTrack.scrollLeft = slide.offsetLeft;
      });
    }

    /* ---------------- small links ---------------- */
    root.addEventListener('click', (event) => {
      const reviews = event.target.closest('[data-p2-reviews-link]');
      const details = event.target.closest('[data-p2-open-details]');
      if (reviews) {
        const target = document.getElementById('judgeme_product_reviews') || document.querySelector('.jdgm-review-widget') || document.querySelector('.p2-proof');
        if (target) {
          event.preventDefault();
          window.scrollTo({ top: target.getBoundingClientRect().top + window.pageYOffset - headerOffset() - 12, behavior: SMOOTH });
        }
      } else if (details) {
        const box = $('[data-p2-details]', root);
        if (box) {
          event.preventDefault();
          box.open = true;
          window.scrollTo({ top: box.getBoundingClientRect().top + window.pageYOffset - headerOffset() - 12, behavior: SMOOTH });
        }
      }
    });

    /* ---------------- shop our mugs ---------------- */
    const mugBox = $('[data-p2-mugs]', root);
    const mugRail = $('[data-p2-mugs-rail]', root);
    function syncMini(formEl) {
      $$('.p2-mini', formEl).forEach((label) => {
        const input = $('input', label);
        label.classList.toggle('is-on', !!(input && input.checked));
      });
      const checked = $('input[name="id"]:checked', formEl) || $('input[name="id"]', formEl);
      const priceOut = $('[data-p2-quick-price]', formEl);
      if (checked && priceOut && checked.dataset.price) priceOut.textContent = checked.dataset.price;
    }
    /* Newest N first, then best sellers, no repeats; top up from the price-ascending list only when a
       team has too few mugs in the first two. The feed itself only outputs Mugs-category / Printful items. */
    async function pickMugs() {
      const limit = Number(data.mugs.limit) || 10;
      const newestCount = Math.max(0, Math.min(limit, Number(data.mugs.newest) || 0));
      const feed = (sort) => loadFeed(data.type, sort, 'bt-p2-mugs-feed').catch(() => []);
      const ok = (c) => c.dataset.kind === 'mug' && sameType(c.dataset.type, data.type) && c.dataset.handle !== data.handle;
      const picks = [];
      const seen = new Set();
      const take = (cards, upTo) => {
        for (const card of cards) {
          if (picks.length >= upTo) break;
          if (!ok(card) || seen.has(card.dataset.handle)) continue;
          seen.add(card.dataset.handle);
          picks.push(card);
        }
      };
      const [newest, best] = await Promise.all([newestCount ? feed('created-descending') : Promise.resolve([]), feed('best-selling')]);
      take(newest, newestCount);
      take(best, limit);
      if (picks.length < limit) take(await feed('price-ascending'), limit);
      return picks;
    }
    if (mugBox && mugRail && data.type && data.mugs && data.mugs.enabled) {
      pickMugs().then((picks) => {
        if (!picks.length) return;
        picks.forEach((card) => mugRail.appendChild(document.importNode(card, true)));
        $$('[data-p2-quick]', mugRail).forEach(syncMini);
        revealWithoutJump(mugBox.nextElementSibling, () => {
          mugBox.hidden = false;
          railNav(mugRail, $('[data-p2-mugs-prev]', mugBox), $('[data-p2-mugs-next]', mugBox));
        });
      }).catch(() => { /* stays hidden */ });
      mugRail.addEventListener('change', (event) => {
        const formEl = event.target.closest('[data-p2-quick]');
        if (formEl) syncMini(formEl);
      });
      mugRail.addEventListener('submit', async (event) => {
        const formEl = event.target.closest('[data-p2-quick]');
        if (!formEl) return;
        event.preventDefault();
        const checked = $('input[name="id"]:checked', formEl) || $('input[name="id"]', formEl);
        const btn = $('.p2-mug__add', formEl);
        const label = $('.p2-mug__add-label', formEl);
        if (!checked || !btn || btn.classList.contains('is-busy')) return;
        btn.classList.add('is-busy');
        try {
          await Cart.add([{ id: Number(checked.value), quantity: 1 }]);
          btn.classList.add('is-added');
          if (label) label.textContent = 'Added';
          announce('Mug added to your cart.');
          Cart.openDrawer();
          window.setTimeout(() => { btn.classList.remove('is-added'); if (label) label.textContent = 'Add'; }, 1800);
        } catch (err) {
          announce(err.message);
          if (label) label.textContent = 'Try again';
        } finally {
          btn.classList.remove('is-busy');
        }
      });
    }

    /* ---------------- header offset for the sticky desktop gallery ---------------- */
    const setHeaderVar = () => {
      const header = headerEl();
      if (header && gallery) root.style.setProperty('--p2-header', header.offsetHeight + 'px');
    };
    setHeaderVar();
    window.addEventListener('resize', setHeaderVar, { passive: true });

    /* ---------------- recently viewed (same key the theme uses) ---------------- */
    try {
      const key = 'yv-recently-viewed';
      const list = JSON.parse(window.localStorage.getItem(key) || '[]');
      if (list.indexOf(data.productId) === -1) list.unshift(data.productId);
      window.localStorage.setItem(key, JSON.stringify(list.slice(0, 14)));
    } catch (e) { /* storage blocked */ }

    hookThemeCart();
    window.addEventListener('load', hookThemeCart, { once: true });
    render();
  }

  /* Pre-boot, the Add to cart button is disabled by an inline script so a tap can't post the
     default variant. Re-enable here (also on failure, with the plain variant <select> shown). */
  function initProductSafely(root) {
    if (root.dataset.p2Init) return;
    root.dataset.p2Init = '1';
    const atc = $('[data-p2-atc]', root);
    try {
      initProduct(root);
    } catch (err) {
      console.warn('[bt-p2] falling back to the basic form', err);
      root.classList.add('p2-nojs');
    } finally {
      if (atc) { atc.disabled = false; atc.setAttribute('data-p2-ready', ''); }
    }
  }

  /* ======================================================================
     Team rail
     ====================================================================== */
  function initRail(section) {
    if (section.dataset.p2Init) return;
    section.dataset.p2Init = '1';
    const type = section.dataset.type;
    const handle = section.dataset.handle;
    const trackEl = $('[data-p2-rail-track]', section);
    if (!type || !trackEl) return;
    const limit = Number(section.dataset.limit) || 12;
    const sort = section.dataset.sort || 'best-selling';
    const wrapper = section.closest('.shopify-section') || section.parentElement || section;
    const marker = wrapper.nextElementSibling;
    let started = false;
    const start = () => {
      if (started) return;
      started = true;
      loadFeed(type, sort).then((cards) => {
        const picks = cards.filter((c) => c.dataset.kind === 'item' && sameType(c.dataset.type, type) && c.dataset.handle !== handle).slice(0, limit);
        if (picks.length < 2) return;
        picks.forEach((card) => trackEl.appendChild(document.importNode(card, true)));
        revealWithoutJump(marker, () => {
          section.hidden = false;
          railNav(trackEl, $('[data-p2-rail-prev]', section), $('[data-p2-rail-next]', section));
        });
      }).catch(() => { /* stays hidden */ });
    };
    if (!('IntersectionObserver' in window)) { start(); return; }
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) { io.disconnect(); start(); }
    }, { rootMargin: '1200px 0px' });
    io.observe(wrapper);
  }

  function boot(scope = document) {
    $$('[data-bt-p2]', scope).forEach(initProductSafely);
    $$('[data-p2-rail]', scope).forEach(initRail);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => boot(), { once: true });
  else boot();
  /* theme editor: sections are re-rendered in place */
  document.addEventListener('shopify:section:load', (event) => boot(event.target));
})();
